/**
 * useCalendlyTrigger Hook
 *
 * Intercepts clicks on special Calendly URLs and triggers the Calendly popup.
 * This allows Contentful-managed buttons to open Calendly without custom components.
 *
 * Special URL patterns:
 * - #calendly-in-person - Opens in-person consultation booking
 * - #calendly-video - Opens video consultation booking
 *
 * @see CL-007: Showroom Details page integration
 */

import {useEffect, useCallback} from 'react';

// Special URL patterns for Calendly triggers
const CALENDLY_IN_PERSON_HASH = '#calendly-in-person';
const CALENDLY_VIDEO_HASH = '#calendly-video';

/**
 * Generates the Calendly URL by replacing the showroom ID placeholder.
 *
 * @param {string} urlTemplate - URL template with {{calendlyShowroomId}} placeholder
 * @param {string} showroomId - The showroom's Calendly ID
 * @returns {string|null} - The generated URL or null if invalid
 */
const generateCalendlyUrl = (urlTemplate, showroomId) => {
  if (!urlTemplate || !showroomId) return null;

  return urlTemplate.replace(/\{\{calendlyShowroomId\}\}/g, showroomId);
};

/**
 * Hook to intercept Calendly trigger URLs and open popup.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether Calendly feature is enabled
 * @param {string} options.calendlyShowroomId - The showroom's Calendly ID
 * @param {string} options.inPersonUrlTemplate - URL template for in-person consultations
 * @param {string} options.videoUrlTemplate - URL template for video consultations
 */
export const useCalendlyTrigger = ({
  enabled,
  calendlyShowroomId,
  inPersonUrlTemplate,
  videoUrlTemplate,
}) => {
  // Generate the actual Calendly URLs
  const inPersonUrl = generateCalendlyUrl(inPersonUrlTemplate, calendlyShowroomId);
  const videoUrl = generateCalendlyUrl(videoUrlTemplate, calendlyShowroomId);

  // Open Calendly popup using global Calendly object
  const openCalendlyPopup = useCallback((url) => {
    if (!url) {
      console.warn('[Calendly] Cannot open popup: No URL provided');

      return;
    }

    // Calendly popup options
    const popupOptions = {
      url,
    };


    // Use global Calendly object (loaded via script)
    if (typeof window !== 'undefined' && window.Calendly) {
      window.Calendly.initPopupWidget(popupOptions);
    } else {
      // Fallback: Load Calendly script dynamically and then open
      const script = document.createElement('script');

      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => {
        if (window.Calendly) {
          window.Calendly.initPopupWidget(popupOptions);
        }
      };
      document.head.appendChild(script);

      // Also load CSS
      const link = document.createElement('link');

      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, []);

  // Click handler for intercepting Calendly trigger URLs
  const handleClick = useCallback((event) => {
    // Only process if Calendly is enabled and we have a showroom ID
    if (!enabled || !calendlyShowroomId) {
      return;
    }

    // Find the closest anchor element or button
    const target = event.target.closest('a, button');

    if (!target) return;

    const href = target.getAttribute('href');

    if (!href) return;

    // Extract hash from URL (handles both "#calendly-in-person" and "/page#calendly-in-person")
    let hash = '';

    if (href.includes('#')) {
      hash = '#' + href.split('#')[1];
    } else {
      hash = href;
    }

    // Check for Calendly trigger patterns
    if (hash === CALENDLY_IN_PERSON_HASH) {
      event.preventDefault();
      event.stopPropagation();
      openCalendlyPopup(inPersonUrl);

      return false;
    } else if (hash === CALENDLY_VIDEO_HASH) {
      event.preventDefault();
      event.stopPropagation();
      openCalendlyPopup(videoUrl);

      return false;
    }
  }, [enabled, calendlyShowroomId, inPersonUrl, videoUrl, openCalendlyPopup]);

  // Handle hash changes (for React Router navigation)
  const handleHashChange = useCallback(() => {
    const hash = window.location.hash;

    if (hash === CALENDLY_IN_PERSON_HASH) {
      // Clear the hash to prevent re-triggering
      window.history.replaceState(null, '', window.location.pathname);
      openCalendlyPopup(inPersonUrl);
    } else if (hash === CALENDLY_VIDEO_HASH) {
      // Clear the hash to prevent re-triggering
      window.history.replaceState(null, '', window.location.pathname);
      openCalendlyPopup(videoUrl);
    }
  }, [inPersonUrl, videoUrl, openCalendlyPopup]);

  // Check hash on mount and when it changes
  useEffect(() => {
    if (!enabled || !calendlyShowroomId) {
      return;
    }

    // Check current hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [enabled, calendlyShowroomId, handleHashChange]);

  // Add click listener to document
  useEffect(() => {
    if (!enabled || !calendlyShowroomId) {
      // Log warning only if partially configured (enabled but missing showroomId)
      if (enabled && !calendlyShowroomId) {
        console.warn('[Calendly] Feature enabled but store is missing c_calendlyShowroomId attribute');
      }

      return;
    }

    // Use capture phase to intercept before other handlers
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [enabled, calendlyShowroomId, handleClick]);

  // Return URLs for manual triggering if needed
  return {
    inPersonUrl,
    videoUrl,
    openInPersonPopup: () => openCalendlyPopup(inPersonUrl),
    openVideoPopup: () => openCalendlyPopup(videoUrl),
  };
};

export default useCalendlyTrigger;
